import React from "react";
import { MedicalServices, AccountCircle } from "@mui/icons-material";
import { Stack, Typography, Box, Tooltip } from "@mui/material";
import { useSessionContext } from "@/contexts/SessionContext";
import { useViewContext } from "@/contexts/ViewContext";
import { useNavigate } from "react-router-dom";

interface ViewSwitcherProps {
  isDrawerOpen: boolean;
}

export function ViewSwitcher({ isDrawerOpen }: ViewSwitcherProps) {
  const { access, setAccess } = useSessionContext();
  const { changeView } = useViewContext();
  const navigate = useNavigate();

  /**
   * No mini-drawer (colapsado), o layout horizontal com `width: 100%` em cada opção
   * vira um anti-pattern: reduz área de clique real e quebra alinhamento visual.
   *
   * Regra:
   * - `isDrawerOpen: true`  → modo expandido (ícone + texto, horizontal)
   * - `isDrawerOpen: false` → modo mini (somente ícones, vertical/centrado)
   */
  const isMiniDrawer = !isDrawerOpen;

  const handleViewChange = async (type: "professional" | "patient") => {
    if (type === "patient") {
      setAccess({ type: "patient" });
      navigate("/");
    } else {
      changeView();
    }
  };

  const buildOptionSx = (isSelected: boolean) => {
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: isMiniDrawer ? "center" : "flex-start",
      gap: isMiniDrawer ? 0 : 1.5,
      p: 1,
      borderRadius: 1,
      cursor: "pointer",
      bgcolor: isSelected ? "primary700.main" : "transparent",
      color: isSelected ? "white" : "neutral600.main",
      transition: "all 0.2s",
      "&:hover": {
        bgcolor: isSelected ? "primary700.main" : "rgba(0, 168, 132, 0.08)",
      },
      ...(isMiniDrawer
        ? {
            width: 40,
            height: 40,
          }
        : {
            width: "100%",
          }),
    } as const;
  };

  return (
    <Stack
      spacing={1.5}
      p={2}
      sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction={isMiniDrawer ? "column" : "row"}
        spacing={1}
        sx={{
          px: 0.5,
          alignItems: isMiniDrawer ? "center" : "stretch",
        }}
      >
        <Tooltip title="Visão Profissional" placement="right">
          <Box
            onClick={() => handleViewChange("professional")}
            sx={buildOptionSx(access.type === "professional")}
          >
            <MedicalServices fontSize="small" />
            {!isMiniDrawer && (
              <Typography
                variant="body2"
                sx={{
                  opacity: 1,
                  transition: "opacity 0.2s",
                  whiteSpace: "nowrap",
                }}
              >
                Profissional
              </Typography>
            )}
          </Box>
        </Tooltip>

        <Tooltip title="Visão Paciente" placement="right">
          <Box
            onClick={() => handleViewChange("patient")}
            sx={buildOptionSx(access.type === "patient")}
          >
            <AccountCircle fontSize="small" />
            {!isMiniDrawer && (
              <Typography
                variant="body2"
                sx={{
                  opacity: 1,
                  transition: "opacity 0.2s",
                  whiteSpace: "nowrap",
                }}
              >
                Paciente
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
